/**
 * Add / edit a contact in the realtor's address book (#332).
 *
 * One form for both: with `contact` it prefills and saves through
 * useUpdateContact, without it saves through useCreateContact. A save the
 * server refuses (a duplicate phone number is a 409) keeps the dialog open
 * and shows the server's message above the buttons.
 */

import { Loader2 } from "lucide-react";
import { type FormEvent, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
	COUNTRY_CODES,
	DEFAULT_COUNTRY_CODE,
} from "@/features/auth/utils/phone";
import {
	useCreateContact,
	useUpdateContact,
} from "@/features/realtor/hooks/use-realtor-crm";
import {
	CONTACT_TYPES,
	type Contact,
	type ContactInput,
	type ContactType,
} from "@/features/realtor/services/realtor-crm-api.service";
import { StudioButton } from "@/features/studio/theme";

interface ContactFormDialogProps {
	/** The contact to edit; omit to add a new one. */
	contact?: Contact;
	/** Type preselected for a new contact. */
	defaultType?: ContactType;
	onClose: () => void;
}

interface FormValues {
	name: string;
	type: ContactType;
	phoneCountryCode: string;
	phone: string;
	email: string;
	city: string;
	notes: string;
}

type FieldErrors = Partial<Record<"name" | "phone" | "email", string>>;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function initialValues(
	contact: Contact | undefined,
	defaultType: ContactType,
): FormValues {
	return {
		name: contact?.name ?? "",
		type: contact?.type ?? defaultType,
		phoneCountryCode: contact?.phoneCountryCode ?? DEFAULT_COUNTRY_CODE,
		phone: contact?.phone ?? "",
		email: contact?.email ?? "",
		city: contact?.city ?? "",
		notes: contact?.notes ?? "",
	};
}

/** Blank optional fields go to the server as null, which clears them. */
function toInput(values: FormValues): ContactInput {
	const optional = (value: string) => value.trim() || null;
	return {
		name: values.name.trim(),
		type: values.type,
		phone: optional(values.phone),
		phoneCountryCode: values.phoneCountryCode,
		email: optional(values.email),
		city: optional(values.city),
		notes: optional(values.notes),
	};
}

export function ContactFormDialog({
	contact,
	defaultType = "buyer",
	onClose,
}: ContactFormDialogProps) {
	const { t } = useTranslation();
	const formId = useId();
	const [values, setValues] = useState<FormValues>(() =>
		initialValues(contact, defaultType),
	);
	const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
	const createContact = useCreateContact();
	const updateContact = useUpdateContact();
	const mutation = contact ? updateContact : createContact;
	const isEdit = Boolean(contact);

	// The login screen's four codes, plus this contact's own if it is another.
	const countryCodes: string[] = COUNTRY_CODES.includes(
		values.phoneCountryCode as (typeof COUNTRY_CODES)[number],
	)
		? [...COUNTRY_CODES]
		: [values.phoneCountryCode, ...COUNTRY_CODES];

	const set =
		(field: keyof FormValues) =>
		(
			event: React.ChangeEvent<
				HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
			>,
		) => {
			const next = event.target.value;
			setValues((current) => ({ ...current, [field]: next }));
			if (field in fieldErrors) {
				setFieldErrors((current) => ({ ...current, [field]: undefined }));
			}
		};

	const validate = (): FieldErrors => {
		const errors: FieldErrors = {};
		if (!values.name.trim()) {
			errors.name = t("realtor.contacts.form.errors.nameRequired");
		}
		if (values.phone.trim() && !/\d/.test(values.phone)) {
			errors.phone = t("realtor.contacts.form.errors.phoneDigits");
		}
		if (values.email.trim() && !EMAIL_PATTERN.test(values.email.trim())) {
			errors.email = t("realtor.contacts.form.errors.emailInvalid");
		}
		return errors;
	};

	const handleSubmit = (event: FormEvent) => {
		event.preventDefault();
		if (mutation.isPending) return;
		const errors = validate();
		setFieldErrors(errors);
		if (Object.values(errors).some(Boolean)) return;
		const input = toInput(values);
		const onSuccess = () => {
			toast.success(
				t(
					isEdit
						? "realtor.contacts.form.updatedToast"
						: "realtor.contacts.form.createdToast",
				),
			);
			onClose();
		};
		if (contact) {
			updateContact.mutate({ id: contact.id, input }, { onSuccess });
		} else {
			createContact.mutate(input, { onSuccess });
		}
	};

	const fieldId = (name: string) => `${formId}-${name}`;

	return (
		<Dialog open onOpenChange={(open) => !open && onClose()}>
			<DialogContent className="max-h-[90vh] w-[calc(100%-2rem)] overflow-y-auto rounded-xl bg-dashboard-bg border-dashboard-border sm:w-full">
				<DialogHeader>
					<DialogTitle className="font-display text-xl italic font-medium text-dashboard-text">
						{t(
							isEdit
								? "realtor.contacts.form.editTitle"
								: "realtor.contacts.form.createTitle",
						)}
					</DialogTitle>
					<DialogDescription className="text-dashboard-muted">
						{t("realtor.contacts.form.description")}
					</DialogDescription>
				</DialogHeader>

				<form
					id={formId}
					onSubmit={handleSubmit}
					noValidate
					className="space-y-4"
				>
					<Field
						id={fieldId("name")}
						label={t("realtor.contacts.form.nameLabel")}
						error={fieldErrors.name}
					>
						<Input
							id={fieldId("name")}
							value={values.name}
							onChange={set("name")}
							placeholder={t("realtor.contacts.form.namePlaceholder")}
							maxLength={200}
							autoComplete="off"
							aria-invalid={Boolean(fieldErrors.name)}
						/>
					</Field>

					<Field
						id={fieldId("type")}
						label={t("realtor.contacts.form.typeLabel")}
					>
						<select
							id={fieldId("type")}
							value={values.type}
							onChange={set("type")}
							className={SELECT_CLASS}
						>
							{CONTACT_TYPES.map((type) => (
								<option key={type} value={type}>
									{t(`realtor.contacts.types.${type}`)}
								</option>
							))}
						</select>
					</Field>

					<Field
						id={fieldId("phone")}
						label={t("realtor.contacts.form.phoneLabel")}
						optional={t("realtor.contacts.form.optional")}
						error={fieldErrors.phone}
					>
						<div className="flex gap-2">
							<select
								aria-label={t("realtor.contacts.form.countryCodeLabel")}
								value={values.phoneCountryCode}
								onChange={set("phoneCountryCode")}
								className={`${SELECT_CLASS} w-24 shrink-0`}
							>
								{countryCodes.map((code) => (
									<option key={code} value={code}>
										+{code}
									</option>
								))}
							</select>
							<Input
								id={fieldId("phone")}
								type="tel"
								inputMode="tel"
								value={values.phone}
								onChange={set("phone")}
								placeholder={t("realtor.contacts.form.phonePlaceholder")}
								maxLength={20}
								className="min-w-0 flex-1"
								aria-invalid={Boolean(fieldErrors.phone)}
							/>
						</div>
					</Field>

					<div className="grid gap-4 sm:grid-cols-2">
						<Field
							id={fieldId("email")}
							label={t("realtor.contacts.form.emailLabel")}
							optional={t("realtor.contacts.form.optional")}
							error={fieldErrors.email}
						>
							<Input
								id={fieldId("email")}
								type="email"
								value={values.email}
								onChange={set("email")}
								maxLength={320}
								aria-invalid={Boolean(fieldErrors.email)}
							/>
						</Field>
						<Field
							id={fieldId("city")}
							label={t("realtor.contacts.form.cityLabel")}
							optional={t("realtor.contacts.form.optional")}
						>
							<Input
								id={fieldId("city")}
								value={values.city}
								onChange={set("city")}
								maxLength={120}
							/>
						</Field>
					</div>

					<Field
						id={fieldId("notes")}
						label={t("realtor.contacts.form.notesLabel")}
						optional={t("realtor.contacts.form.optional")}
					>
						<Textarea
							id={fieldId("notes")}
							value={values.notes}
							onChange={set("notes")}
							placeholder={t("realtor.contacts.form.notesPlaceholder")}
							maxLength={5000}
							rows={3}
						/>
					</Field>

					{mutation.error && (
						<p
							role="alert"
							className="rounded-md px-3 py-2 text-sm"
							style={{
								color: "var(--color-dashboard-pill-risk)",
								backgroundColor: "var(--color-dashboard-pill-risk-bg)",
							}}
						>
							{mutation.error.message}
						</p>
					)}
				</form>

				<DialogFooter className="gap-2">
					<StudioButton
						variant="quiet"
						size="sm"
						onClick={onClose}
						disabled={mutation.isPending}
					>
						{t("realtor.contacts.form.cancel")}
					</StudioButton>
					<StudioButton
						type="submit"
						form={formId}
						size="sm"
						className="gap-1.5"
						disabled={mutation.isPending}
					>
						{mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
						{mutation.isPending
							? t("realtor.contacts.form.saving")
							: t(
									isEdit
										? "realtor.contacts.form.update"
										: "realtor.contacts.form.create",
								)}
					</StudioButton>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

const SELECT_CLASS =
	"h-10 rounded-md border border-dashboard-border bg-dashboard-bg px-3 text-sm text-dashboard-text focus:outline-none focus:ring-2 focus:ring-ring";

function Field({
	id,
	label,
	optional,
	error,
	children,
}: {
	id: string;
	label: string;
	optional?: string;
	error?: string;
	children: React.ReactNode;
}) {
	return (
		<div className="space-y-1.5">
			<Label htmlFor={id} className="text-xs font-medium text-dashboard-text">
				{label}
				{optional && " "}
				{optional && (
					<span className="font-normal text-dashboard-muted">({optional})</span>
				)}
			</Label>
			{children}
			{error && (
				<p
					className="text-xs"
					style={{ color: "var(--color-dashboard-pill-risk)" }}
				>
					{error}
				</p>
			)}
		</div>
	);
}
